import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Paper,
  IconButton,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Image,
  PictureAsPdf,
} from '@mui/icons-material';
import { apiService } from '../services/api';

interface FileUploadProps {
  onFileUploaded: (fileData: {
    s3Key: string;
    s3Url: string;
    originalName: string;
    mimeType: string;
    fileSize: number;
  }) => void;
  onFileRemoved: () => void;
  currentFile?: {
    s3Key: string;
    s3Url: string;
    originalName: string;
    mimeType: string;
    fileSize: number;
  };
  accept?: {
    [key: string]: string[];
  };
  maxSize?: number;
  label?: string;
  helperText?: string;
  donationId?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFileUploaded,
  onFileRemoved,
  currentFile,
  accept = {
    'image/*': ['.jpeg', '.jpg', '.png', '.webp'],
  },
  maxSize = 10 * 1024 * 1024, // 10MB
  label = 'Trascina qui il file o clicca per selezionare',
  helperText = 'Supportati: JPG, PNG, WebP (max 10MB)',
  donationId,
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      setError(null);
      setUploading(true);

      try {
        const formData = new FormData();
        formData.append('file', file);
        if (donationId) {
          formData.append('donationId', donationId);
        }

        const response = await apiService.uploadDocumentPhoto(formData);
        onFileUploaded(response);
      } catch (err: any) {
        setError(err.message || 'Errore durante il caricamento del file');
      } finally {
        setUploading(false);
      }
    },
    [onFileUploaded, donationId]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxSize,
    multiple: false,
    disabled: uploading,
  });

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) {
      return <Image color="primary" />;
    }
    if (mimeType === 'application/pdf') {
      return <PictureAsPdf color="error" />;
    }
    return <CloudUpload />;
  };

  return (
    <Box>
      {currentFile ? (
        <Box>
          {/* Anteprima immagine se è un'immagine */}
          {currentFile.mimeType.startsWith('image/') && (currentFile.s3Url || currentFile.s3Key) && (
            <Box
              sx={{
                mb: 2,
                textAlign: 'center',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <Box
                component="img"
                src={currentFile.s3Key ? apiService.getImageUrl(currentFile.s3Key) : currentFile.s3Url}
                alt={currentFile.originalName}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '200px',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </Box>
          )}
          
          {/* Informazioni file */}
          <Paper
            elevation={2}
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: 'success.light',
              color: 'success.contrastText',
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              {getFileIcon(currentFile.mimeType)}
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  {currentFile.originalName}
                </Typography>
                <Typography variant="caption">
                  {formatFileSize(currentFile.fileSize)}
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={onFileRemoved}
              sx={{ color: 'error.main' }}
            >
              <Delete />
            </IconButton>
          </Paper>
        </Box>
      ) : (
        <Paper
          {...getRootProps()}
          elevation={isDragActive ? 8 : 2}
          sx={{
            p: 3,
            textAlign: 'center',
            cursor: uploading ? 'not-allowed' : 'pointer',
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'grey.300',
            backgroundColor: isDragActive ? 'primary.light' : 'background.paper',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'primary.light',
            },
          }}
        >
          <input {...getInputProps()} />
          <CloudUpload
            sx={{
              fontSize: 48,
              color: isDragActive ? 'primary.main' : 'grey.400',
              mb: 2,
            }}
          />
          <Typography variant="h6" gutterBottom>
            {isDragActive ? 'Rilascia il file qui' : label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {helperText}
          </Typography>
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="caption" sx={{ mt: 1 }}>
                Caricamento in corso...
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default FileUpload;
